import * as React from "react";
import { Typography } from "antd";
import { logger } from "@/helpers/tslog";
import type { ContentSlidePrimaryCompany } from "@/state/contentSlideStore";
import { usePresentationStore } from "@/state/presentationStore";
import { CompanySearchField } from "@/Popups/contentSlide/components/CompanySearchField";
import { PeersCatalogInfoButton } from "@/Popups/contentSlide/components/PeersCatalogInfoButton";
import PresentationChartTypeRadioList, { type ChartPresentationTypeId } from "./PresentationChartTypeRadioList";

const { Title } = Typography;

/** Placeholder step before Initial Input; replace with real chart-type UI later. */
export default function PresentationChartTypeStep() {
  const currentStep = usePresentationStore((s) => s.currentStep);
  const setAnswersByStep = usePresentationStore((s) => s.setAnswersByStep);
  const questionsPerStep = usePresentationStore((s) => s.questionsPerStep);
  const primaryCompany = usePresentationStore((s) => s.primaryCompany);
  const setPrimaryCompany = usePresentationStore((s) => s.setPrimaryCompany);
  const selectedPresentationChartTypeId = usePresentationStore((s) => s.selectedPresentationChartTypeId);
  const setSelectedPresentationChartTypeId = usePresentationStore((s) => s.setSelectedPresentationChartTypeId);

  const handleSelect = React.useCallback(
    (payload: ContentSlidePrimaryCompany) => {
      setPrimaryCompany(payload);
      logger.info("[PresentationChartTypeStep] primary company stored", payload);
    },
    [setPrimaryCompany]
  );

  React.useEffect(() => {
    const chartOk = selectedPresentationChartTypeId != null && selectedPresentationChartTypeId !== "";
    const companyOk = Boolean(primaryCompany?.companyName?.trim());
    const complete = chartOk && companyOk;
    setAnswersByStep(currentStep, complete ? (questionsPerStep[currentStep] ?? 1) : 0);
  }, [currentStep, questionsPerStep, primaryCompany, selectedPresentationChartTypeId, setAnswersByStep]);

  const companySelected = Boolean(primaryCompany?.companyName?.trim());

  return (
    <div className="box-border h-full min-h-0 overflow-y-auto p-6 text-gray-800">
      <div className="w-[300px] leading-none">
        <div className="leading-normal text-gray-900">
          <div className="flex items-center gap-1">
            <div className="min-w-0 flex-1">
              <CompanySearchField
                displayText={primaryCompany?.companyName || "Select a company"}
                onSelectCompany={handleSelect}
                className="w-full"
              />
            </div>
            <PeersCatalogInfoButton />
          </div>
        </div>
      </div>

      {companySelected ? (
        <>
          <Title level={4} className="!mb-2 mt-10">
            Select a Presentation Type
          </Title>
          <p className="text-sm text-gray-600 mt-2 mb-6">
            Choose the type of presentation you want to build for the selected company.
          </p>
          <PresentationChartTypeRadioList
            value={selectedPresentationChartTypeId}
            onChange={(id: ChartPresentationTypeId) => setSelectedPresentationChartTypeId(id)}
          />
        </>
      ) : null}
    </div>
  );
}
